import React from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, InlineEditableText
} from './ThemeLayoutHelpers';

export default function VitalityLayout({ p }) {
  const T = p.storeTheme || {};
  const primary = T.primaryColor || "#10b981";
  const text = p.storefrontText || {};
  const copy = getHeroContent(p, {
    eyebrow: "Fuel Your Body",
    title: "Pure Energy. Peak Performance.",
    subtitle: "Premium health, wellness, and fitness essentials built to elevate your daily routine.",
    primaryCta: "Explore Wellness",
    secondaryCta: "View Products",
  });

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-[#f0fdf4] via-[#e6f4ea] to-[#f4fbf7]" style={{ fontFamily: T.fontFamily || "Poppins, sans-serif" }}>
      {/* Premium background glowing circles */}
      <div className="fixed top-12 left-1/4 w-80 h-80 bg-emerald-300/20 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-24 right-1/4 w-[400px] h-[400px] bg-teal-300/10 rounded-full blur-3xl pointer-events-none" />

      {/* Modern Navigation Header */}
      <nav className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-emerald-100/50 shadow-[0_2px_15px_rgba(16,185,129,0.03)]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <StoreLogo storeInfo={p.storeInfo} border="border-2 border-emerald-400" />
            <div>
              <p className="font-bold text-emerald-950 text-base leading-tight">{p.storeInfo?.businessName}</p>
              <p className="text-[10px] text-emerald-600 font-semibold tracking-wider uppercase mt-0.5">{p.storeInfo?.businessCategory || "Health & Wellness"}</p>
            </div>
          </div>
          <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="flex-1 max-w-lg hidden md:block" />
          <div className="flex items-center gap-3">
            <button onClick={p.handleGoToWishlist} className="p-2.5 rounded-full hover:bg-emerald-50 text-emerald-700 transition relative border border-emerald-100/50 bg-white/50">
              <FaHeart className="text-emerald-600 text-lg" />
              {p.wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] flex items-center justify-center font-bold">
                  {p.wishlistCount}
                </span>
              )}
            </button>
            <CartBtn p={p} />
            {p.userMenuElement}
          </div>
        </div>
      </nav>

      {/* Main Hero Showcase */}
      <section className="max-w-7xl mx-auto w-full px-6 pt-8">
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-center">
          <div className="space-y-6">
            <div>
              <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-800 text-xs font-bold tracking-wide mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
                <InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText>
              </span>
              <h1 className="text-4xl md:text-5xl font-black text-emerald-950 leading-tight">
                <InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText>
              </h1>
              <p className="mt-4 text-emerald-900/70 text-base md:text-lg leading-relaxed">
                <InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText>
              </p>
            </div>

            {/* Health & Fitness Stats Row */}
            <div className="grid grid-cols-3 gap-3 md:gap-4 bg-white/60 backdrop-blur-md rounded-2xl p-4 border border-emerald-100 shadow-sm">
              {[
                { value: "Clean Labs", desc: "100% Certified", emoji: "🔬" },
                { value: p.filteredProducts?.length || 0, desc: "Total Active Picks", emoji: "⚡" },
                { value: "Fast Shipping", desc: "Doorstep Delivery", emoji: "📦" }
              ].map((item, idx) => (
                <div key={idx} className="text-center space-y-1">
                  <div className="text-xl">{item.emoji}</div>
                  <div className="font-extrabold text-emerald-900 text-sm md:text-base">{item.value}</div>
                  <div className="text-[10px] text-gray-500 font-medium">{item.desc}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => p.setActiveTab("products")}
                className="px-6 py-3 rounded-xl text-white font-bold hover:opacity-90 transition shadow-lg shadow-emerald-500/20 text-sm"
                style={{ backgroundColor: primary }}
              >
                <InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText>
              </button>
              <button
                onClick={() => p.setActiveTab("details")}
                className="px-6 py-3 rounded-xl border border-emerald-200 text-emerald-800 font-bold hover:bg-emerald-50 transition bg-white/40 text-sm"
              >
                <InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText>
              </button>
            </div>
          </div>

          <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white/60 bg-white/20 backdrop-blur-sm">
            <HeroImage p={p} themeId="vitality" className="h-[320px] md:h-[380px]">
              <div className="absolute inset-0 bg-gradient-to-tr from-emerald-950/20 via-transparent to-transparent pointer-events-none" />
            </HeroImage>
          </div>
        </div>
      </section>

      {/* Tabs Selector */}
      <section className="max-w-7xl mx-auto w-full px-6 mt-10">
        <div className="flex gap-2 border-b border-emerald-100 overflow-x-auto pb-px">
          {[
            { id: "products", label: text.products || "Products", icon: "🍏" },
            { id: "services", label: text.services || "Services", icon: "💆" },
            { id: "details", label: "Store Details", icon: "ℹ️" }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => p.setActiveTab(tab.id)}
              className={`px-5 py-3 text-sm font-bold flex items-center gap-2 border-b-2 transition whitespace-nowrap ${
                p.activeTab === tab.id
                  ? 'border-emerald-600 text-emerald-800'
                  : 'border-transparent text-emerald-700/60 hover:text-emerald-800'
              }`}
            >
              <span>{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {/* Tab Contents */}
      <section className="flex-1 max-w-7xl mx-auto w-full px-6 py-6">
        <TabContent p={p} />
      </section>

      <StoreFooter p={p} />
    </div>
  );
}
